import type { ApiResponse, Stash, StashWithAssets, GalleryResponse, RecentStash, ApiError as ApiErrorType } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.dostash.com';

export class ApiError extends Error implements ApiErrorType {
  constructor(
    public code: string,
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ✅ FIXED: Helper to parse API responses with proper data nesting
async function parseResponse<T>(response: Response): Promise<T> {
  const json: ApiResponse<T> = await response.json();
  
  if (!json.success) {
    throw new ApiError(
      json.error?.code || 'UNKNOWN_ERROR',
      response.status,
      json.error?.message || 'An error occurred'
    );
  }
  
  // ✅ FIXED: Return json.data instead of json
  return json.data as T;
}

export const api = {
  // Create a demo stash with sample content
  async createDemoStash(): Promise<{ slug: string; password: string; token: string; assetCount: number }> {
    const response = await fetch(`${API_BASE}/stashes/demo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    return parseResponse<{ slug: string; password: string; token: string; assetCount: number }>(response);
  },

  // Create a new stash
  async createStash(
    title?: string,
    description?: string,
    slug?: string,
    password?: string
  ): Promise<Stash> {
    const response = await fetch(`${API_BASE}/stashes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title, description, slug, password }),
    });
    
    return parseResponse<Stash>(response);
  },

  // Get a stash by slug
  async getStash(slug: string): Promise<StashWithAssets> {
    const response = await fetch(`${API_BASE}/stashes/${slug}`, {
      credentials: 'include',
    });
    
    if (response.status === 404) {
      throw new ApiError('NOT_FOUND', 404, 'Stash not found');
    }
    
    return parseResponse<StashWithAssets>(response);
  },

  // Unlock a stash with password
  async unlockStash(slug: string, password: string): Promise<{ token: string; slug: string }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    
    return parseResponse<{ token: string; slug: string }>(response);
  },

  // Add an asset to a stash (requires auth token from unlock)
  async addAsset(
    slug: string,
    asset: {
      type: string;
      url?: string;
      title?: string;
      description?: string;
    },
    token: string
  ): Promise<{ id: string; position: number }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/assets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ asset }),
    });

    return parseResponse<{ id: string; position: number }>(response);
  },

  // Publish a stash (generates static HTML page)
  async publishStash(slug: string, token: string): Promise<{ published: boolean; slug: string }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    return parseResponse<{ published: boolean; slug: string }>(response);
  },

  // Delete an asset from a stash (requires auth token)
  async deleteAsset(slug: string, assetId: string, token: string): Promise<{ deleted: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/assets/${assetId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    return parseResponse<{ deleted: boolean }>(response);
  },

  // Reorder assets in a stash (requires auth token)
  async reorderAssets(slug: string, assetIds: string[], token: string): Promise<{ reordered: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/assets/reorder`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ assetIds }),
    });

    return parseResponse<{ reordered: boolean }>(response);
  },

  // Update stash title and description (requires auth token)
  async updateStash(
    slug: string,
    data: { title?: string; description?: string; theme?: string },
    token: string
  ): Promise<{ updated: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return parseResponse<{ updated: boolean }>(response);
  },

  // Change stash password (requires auth token + current password)
  async changePassword(
    slug: string,
    currentPassword: string,
    newPassword: string,
    token: string
  ): Promise<{ updated: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    return parseResponse<{ updated: boolean }>(response);
  },

  // Delete a stash and all its content (requires auth token)
  async deleteStash(slug: string, token: string): Promise<{ deleted: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    return parseResponse<{ deleted: boolean }>(response);
  },

  // Clone a stash into a new one
  async cloneStash(
    sourceSlug: string,
    newSlug: string,
    password: string
  ): Promise<{ slug: string; url: string; assetsCloned: number }> {
    const response = await fetch(`${API_BASE}/stashes/${sourceSlug}/clone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: newSlug, password }),
    });

    return parseResponse<{ slug: string; url: string; assetsCloned: number }>(response);
  },

  // Submit a contact message
  async submitContact(
    category: string,
    message: string,
    email?: string
  ): Promise<{ messageId: string }> {
    const response = await fetch(`${API_BASE}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, message, email: email || undefined }),
    });

    return parseResponse<{ messageId: string }>(response);
  },

  // Validate a URL
  async validateUrl(url: string): Promise<{ valid: boolean; status: number; error?: string; warning?: string }> {
    const response = await fetch(`${API_BASE}/validate-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ url }),
    });
    return parseResponse<{ valid: boolean; status: number; error?: string; warning?: string }>(response);
  },

  // Set or remove view password (requires VIP plan)
  async setViewPassword(slug: string, password: string, token: string): Promise<{ updated: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/view-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ password }),
    });
    return parseResponse<{ updated: boolean }>(response);
  },

  async removeViewPassword(slug: string, token: string): Promise<{ updated: boolean }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/view-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ remove: true }),
    });
    return parseResponse<{ updated: boolean }>(response);
  },

  // Get public stats (stash counter for homepage)
  async getPublicStats(): Promise<{ totalStashes: number; totalAssets: number }> {
    const response = await fetch(`${API_BASE}/stats/public`);
    return parseResponse<{ totalStashes: number; totalAssets: number }>(response);
  },

  // Get public gallery
  async getGallery(page = 1, limit = 12, sort: 'featured' | 'newest' = 'featured'): Promise<GalleryResponse> {
    const response = await fetch(`${API_BASE}/gallery?page=${page}&limit=${limit}&sort=${sort}`);
    return parseResponse<GalleryResponse>(response);
  },

  // Get recent stashes (live feed)
  async getRecentStashes(limit = 10): Promise<{ stashes: RecentStash[] }> {
    const response = await fetch(`${API_BASE}/stashes/recent?limit=${limit}`);
    return parseResponse<{ stashes: RecentStash[] }>(response);
  },

  // Report a stash
  async reportStash(
    slug: string,
    reason: string,
    details?: string,
    contact?: string
  ): Promise<{ reportId: string }> {
    const response = await fetch(`${API_BASE}/stashes/${slug}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        reason,
        details,
        contact,
        attestation: true,
      }),
    });
    
    return parseResponse<{ reportId: string }>(response);
  },
};
